import { ApiResponse, BaseBodyResponse } from "api/api.d";
import { api } from "api/api";
import {
  BranchResponse,
  GenerateOTPResponse,
  GetCaptchaResponse,
  GetUserProfile,
  InterestRateResponse,
  LoginResponse,
  PromotionResponse,
  TermResponse,
  WebUserGuideResponse,
  GetFAQsResponse,
} from "@/dto/auth/response/auth-response";
import {
  ChangePasswordResponse,
  ChangePasswordRequest,
  ConfirmChangePassRequest,
  GenerateOTPRequest,
  LoginRequest,
  InitPasswordResponse,
  InitPasswordReq,
  VerifyPasswordReq,
  ConfirmPasswordReq,
  InitFirstLoginRequest,
  InitFirstLoginResponse,
  ConfirmFirstLoginRequest,
  SearchPromotionRequest,
  GetFAQsRequest,
} from "@/dto/auth/request/auth-request";
import { get } from "lodash";
import { RESULT_CODE } from "@/constants/enum";

const BASE_URL = "/authen-service/rest/web/request";

export const authAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<
      ApiResponse<LoginResponse>,
      {
        data: LoginRequest;
        headerOnBody: { [key: string]: string };
      }
    >({
      query: ({ data, headerOnBody }) => ({
        url: BASE_URL,
        method: "POST",
        functionName: "secureLoginWeb",
        data,
        headerOnBody,
      }),
    }),
    getCaptcha: builder.query<ApiResponse<GetCaptchaResponse>, void>({
      query: () => ({
        url: BASE_URL,
        method: "POST",
        functionName: "getCaptcha",
      }),
    }),
    getInterestRate: builder.query<ApiResponse<InterestRateResponse>, void>({
      query: () => ({
        url: "/customer-service/rest/app/request",
        method: "POST",
        functionName: "getInterestRates",
      }),
    }),
    getTermAndConditions: builder.query<ApiResponse<TermResponse>, void>({
      query: () => ({
        url: "/customer-service/rest/app/request",
        method: "POST",
        functionName: "getTermAndConditions",
      }),
    }),
    searchPromotion: builder.query<
      ApiResponse<PromotionResponse>,
      SearchPromotionRequest
    >({
      query: (data) => ({
        url: "/customer-service/rest/app/request",
        method: "POST",
        functionName: "searchPromotion",
        data,
      }),
    }),
    getBranches: builder.query<ApiResponse<BranchResponse>, void>({
      query: () => ({
        url: "/customer-service/rest/app/request",
        method: "POST",
        functionName: "getBranches",
      }),
    }),
    getWebUserGuide: builder.query<ApiResponse<WebUserGuideResponse>, void>({
      query: () => ({
        url: "/customer-service/rest/web/request",
        method: "POST",
        functionName: "getWebUserGuide",
      }),
    }),
    getFaqCategory: builder.query<any, void>({
      query: () => ({
        url: "/customer-service/rest/web/request",
        method: "POST",
        functionName: "getFaqCategory",
        data: {
          isGetChildren: true,
        },
      }),
    }),
    getFAQs: builder.query<ApiResponse<GetFAQsResponse>, GetFAQsRequest>({
      query: (data) => ({
        url: "/customer-service/rest/web/request",
        method: "POST",
        functionName: "getFAQs",
        data,
      }),
    }),
    generateOTP: builder.mutation<
      ApiResponse<GenerateOTPResponse>,
      GenerateOTPRequest
    >({
      query: (data: GenerateOTPRequest) => ({
        url: "/authen-service/rest/app/request",
        method: "POST",
        functionName: "generateOTP",
        data,
      }),
    }),
    logout: builder.mutation<ApiResponse<BaseBodyResponse>, void>({
      query: () => ({
        url: BASE_URL,
        method: "POST",
        functionName: "logoutUser",
      }),
    }),
    initPassword: builder.mutation<
      ApiResponse<InitPasswordResponse>,
      InitPasswordReq
    >({
      query: (data) => ({
        url: BASE_URL,
        method: "POST",
        functionName: "initResetPassword",
        data,
      }),
    }),
    verifyPassword: builder.mutation<ApiResponse<any>, VerifyPasswordReq>({
      query: (data) => ({
        url: BASE_URL,
        method: "POST",
        functionName: "verifyResetPassword",
        data,
      }),
    }),
    confirmResetPassword: builder.mutation<
      ApiResponse<any>,
      ConfirmPasswordReq
    >({
      query: (data) => ({
        url: BASE_URL,
        method: "POST",
        functionName: "confirmResetPassword",
        data,
      }),
    }),
    changePassword: builder.mutation<
      ApiResponse<ChangePasswordResponse>,
      ChangePasswordRequest
    >({
      query: (data) => ({
        url: BASE_URL,
        method: "POST",
        functionName: "changePassword",
        data,
      }),
    }),
    confirmChangePassword: builder.mutation<
      ApiResponse<BaseBodyResponse>,
      ConfirmChangePassRequest
    >({
      query: (data) => ({
        url: BASE_URL,
        method: "POST",
        functionName: "confirmChangePassword",
        data,
      }),
    }),
    getUserProfile: builder.query<ApiResponse<GetUserProfile>, void>({
      query: () => ({
        url: "/customer-service/rest/web/request",
        method: "POST",
        functionName: "getUserProfile",
      }),
      providesTags: (result) => {
        const code = get(result, "body.resultCode", null);
        return code === RESULT_CODE.SUCCESS ? [{ type: "getUserProfile" }] : [];
      },
    }),
    generateQRToken: builder.mutation<
      any,
      { userAgent: string; webSessionId: string }
    >({
      query: (data) => ({
        url: BASE_URL,
        method: "POST",
        functionName: "generateQRToken",
        data,
      }),
    }),
    initLockAccount: builder.mutation<
      ApiResponse<any>,
      {
        accountNumber: string;
      }
    >({
      query: (accountNumber) => ({
        url: "/customer-service/rest/web/request",
        method: "POST",
        functionName: "makerInitCloseUserAccount",
        data: accountNumber,
      }),
    }),
    confirmLockAccount: builder.mutation<
      ApiResponse<any>,
      {
        otpId: string;
        otp: string;
        closeReason: string;
      }
    >({
      query: ({ otpId, otp, closeReason }) => ({
        url: "/customer-service/rest/web/request",
        method: "POST",
        functionName: "makerConfirmCloseUserAccount",
        categoryCode: "CORP_CLOSE_ACCOUNT",
        data: { otpId, otp, closeReason },
      }),
    }),
    // Luồng checker đóng tài khoản dùng initAction/confirmAction trong transferAPI
    // (BE route theo businessType = "CLOSE_ACCOUNT" tới CloseAccountAction).
    // Command "checkerInitCloseUserAccount" KHÔNG có handler @Component ở approval-service
    // nên 2 mutation checkerInitLock/checkerConfirmLock cũ đã được bỏ.
    initFirstLogin: builder.mutation<
      ApiResponse<InitFirstLoginResponse>,
      InitFirstLoginRequest
    >({
      query: (data) => ({
        url: BASE_URL,
        method: "POST",
        functionName: "initFirstLogin",
        data,
      }),
    }),
    confirmFirstLogin: builder.mutation<
      ApiResponse<BaseBodyResponse>,
      ConfirmFirstLoginRequest
    >({
      query: (data) => ({
        url: BASE_URL,
        method: "POST",
        functionName: "confirmFirstLogin",
        data,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useGetCaptchaQuery,
  useGenerateOTPMutation,
  useLogoutMutation,
  useChangePasswordMutation,
  useConfirmChangePasswordMutation,
  useGetUserProfileQuery,
  useGetInterestRateQuery,
  useGetTermAndConditionsQuery,
  useSearchPromotionQuery,
  useGetBranchesQuery,
  useGetWebUserGuideQuery,
  useInitPasswordMutation,
  useVerifyPasswordMutation,
  useConfirmResetPasswordMutation,
  useGetFaqCategoryQuery,
  useGetFAQsQuery,
  useGenerateQRTokenMutation,
  useInitLockAccountMutation,
  useConfirmLockAccountMutation,
  useInitFirstLoginMutation,
  useConfirmFirstLoginMutation,
} = authAPI;
